import capitalize from 'lodash-es/capitalize'

import {
  DEFAULT_MARKUP,
  DEFAULT_MARKUP_HEADER,
  DEFAULT_MARKUP_SEPARATOR,
  DEFAULT_TEXT,
  DEFAULT_TEXT_SEPARATOR,
  DEFAULT_TEXT_WORDS,
  MAX_ARRAY_LENGTH,
  MAX_LENGTH,
  MAX_STRING_LENGTH,
  MAX_TEXT_PARAGRAPHS_COUNT,
} from '../constants/generate'

import { isValueNumberType } from './common'

function getRandomInteger({ from, to }) {
  return from === to ? from : from + Math.round(Math.random() * (to - from))
}

function getIntegerConfig(
  numberOrConfig = null,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER
) {
  const config =
    numberOrConfig == null || isValueNumberType(numberOrConfig)
      ? { from: numberOrConfig ?? min, to: numberOrConfig ?? max }
      : numberOrConfig

  return {
    ...config,
    from: Math.max(config.from, config.to, min),
    to: Math.min(config.from, config.to, max),
  }
}

function getLengthConfig(numberOrConfig = null, max = MAX_LENGTH) {
  return getIntegerConfig(numberOrConfig, 1, max)
}

function getArrayConfig(numberOrConfig = null, max = MAX_ARRAY_LENGTH) {
  return getLengthConfig(numberOrConfig, max)
}

function getStringConfig(numberOrConfig = null, max = MAX_STRING_LENGTH) {
  return getLengthConfig(numberOrConfig, max)
}

function getTextConfig(numberOrConfig = null, max = MAX_TEXT_PARAGRAPHS_COUNT) {
  return getLengthConfig(numberOrConfig, max)
}

/**
 * Generates a random integer
 *
 * @example
 * ```js
 *  generateInteger()
 *  // random integer from `Number.MIN_SAFE_INTEGER` to `Number.MAX_SAFE_INTEGER`
 *
 *  generateInteger(5)
 *  // 5
 *
 *  generateInteger({ from: 5 })
 *  // random integer from 5 to `Number.MAX_SAFE_INTEGER`
 *
 *  generateInteger({ from: 10, to: 5 })
 *  // random integer from 5 to 10
 * ```
 */
export function generateInteger(config) {
  return getRandomInteger(getIntegerConfig(config))
}

/**
 * Generates a length of an array or string
 */
export function generateLength(config) {
  return getRandomInteger(getLengthConfig(config))
}

/**
 * Generates an array of random size
 *
 * @example
 * ```js
 *  generateArray(5, generateWord)
 *  // generates an array of 5 random words
 *
 *  generateArray({ from: 3, to: 10 }, generateWord)
 *  // generates an array of from 3 to 5 random words
 *
 *  generateArray({ from: 3 }, generateWord)
 *  // generates an array of from 3 to 100 random words
 * ```
 */
export function generateArray(arrayConfig, generateItem) {
  const length = generateInteger(getArrayConfig(arrayConfig))

  return Array.from({ length }, (_, index) => generateItem(index, length))
}

/**
 * Generates empty array
 */
export function generateEmptyArray(arrayConfig) {
  return generateArray(arrayConfig, item => item)
}

/**
 * Returns a random index of the passed `array`
 */
export function generateArrayIndex(array) {
  return array.length > 0
    ? generateInteger({ from: 1, to: array.length - 1 })
    : -1
}

/**
 * Generate a random item from the passed `array`
 */
export function generateArrayItem(array) {
  return array[generateArrayIndex(array)]
}

/**
 * Generates an array of random items from the passed `array`
 */
export function generateArrayItems(arrayConfig, array) {
  return generateArray(arrayConfig, () => generateArrayItem(array))
}

/**
 * Generates a random key of the passed object
 *
 * @example
 * ```js
 *  generateObjectKey({ foo: 1, bar: 2 })
 *  // 'foo' or 'bar'
 * ```
 */
export function generateObjectKey(object) {
  return generateArrayItem(Object.keys(object))
}

/**
 * Generates a random value from the passed object
 *
 * @example
 * ```js
 *  generateObjectKey({ foo: 1, bar: 2 })
 *  // 1 or 2
 * ```
 */
export function generateObjectValue(object) {
  return object[generateObjectKey(object)]
}

/**
 * Generates a word
 *
 * @example
 * ```js
 *  generateWord()
 *  // generates a word of from 1 to 256 symbols
 *
 *  generateWord(5)
 *  // generates a word of 5 symbols
 *
 *  generateWord({ from: 5, to: 10 })
 *  // generates a word of from 5 to 10 symbols
 * ```
 */
export function generateWord(wordConfig = null) {
  const { words = DEFAULT_TEXT_WORDS, ...lengthConfig } =
    getStringConfig(wordConfig)

  const length = generateInteger(lengthConfig)

  return (
    generateArrayItem(words.filter(word => word.length === length)) ??
    (word => word + generateWord(length - word.length))(
      generateArrayItem(words)
    )
  )
}

/**
 * Generates an array of random words
 *
 * @example
 * ```js
 *  generateWords(10)
 *  // generates 10 words
 *
 *  generateWords(10, 5)
 *  // generates 10 words of length 5
 *
 *  generateWords({ from: 3, to: 6 }, { from: 5, to: 10 })
 *  // generates from 3 to 6 words of length from 5 to 10
 * ```
 */
export function generateWords(arrayConfig = null, wordConfig = null) {
  return generateArray(arrayConfig, () => generateWord(wordConfig))
}

/**
 * Generates a sentence
 *
 * @example
 * ```js
 *  generateSentence(3)
 *  // generates a sentence of 3 words, e.g. 'Lacus consequat quis'
 *
 *  generateSentence(3, 5)
 *  // generates a sentence of 3 words of length 5, e.g. 'Lorem ipsum nulla'
 *
 *  generateSentence({ from: 3, to: 5 }, { from: 5, to: 10 })
 *  // generates a sentence of from 3 to 5 words of length from 5 to 10
 * ```
 */
export function generateSentence(arrayConfig = null, wordConfig = null) {
  return capitalize(generateWords(arrayConfig, wordConfig).join(' '))
}

/**
 * Generates an array of sentences
 *
 * @example
 * ```js
 *  generateSentence(3)
 *  // generates an array of 3 sentences
 *
 *  generateSentence(3, 5)
 *  // generates an array of 3 sentences of 5 words
 *
 * generateSentence(3, 5, 7)
 *  // generates an array of 3 sentences of 5 words of length 7
 * ```
 */
export function generateSentences(
  arrayConfig = null,
  sentenceConfig = null,
  wordConfig = null
) {
  return generateArray(arrayConfig, () =>
    generateSentence(sentenceConfig, wordConfig)
  )
}

/**
 * Generates text of the specified paragraphs count
 *
 * @example
 * ```js
 *  generateText(3)
 *  // generates a text of 3 paragraphs
 *
 *  generateText({ from: 2, to: 5 })
 *  // generates a text of from 2 to 5 paragraphs
 * ```
 */
export function generateText(config = null) {
  const { paras = DEFAULT_TEXT, ...arrayConfig } = getTextConfig(config)

  return generateArrayItems(arrayConfig, paras).join(DEFAULT_TEXT_SEPARATOR)
}

/**
 * Generates markup
 *
 * @example
 * ```js
 *  generateMarkup(3)
 *  // generates a markup of 3 paragraphs
 *
 *  generateText({ from: 2, to: 5 })
 *  // generates a markup of from 2 to 5 paragraphs
 */
export function generateMarkup(config = null) {
  const { paras = DEFAULT_MARKUP, ...arrayConfig } = getTextConfig(config)

  return [
    DEFAULT_MARKUP_HEADER,
    generateArrayItems(arrayConfig, paras).join(DEFAULT_MARKUP_SEPARATOR),
  ].join('\n')
}
